import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimationControls, useReducedMotion } from "framer-motion";
import EditIcon from "@mui/icons-material/Edit";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ShareIcon from "@mui/icons-material/Share";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useApi from "../../hooks/useApi";
import { useAuth } from "../../context/authContext";
import { isStandoutsList } from "../../utils/listSchema";
import { toast } from "react-toastify";
import {
  invalidateListCaches,
  invalidateSavedCollections,
} from "../../utils/cacheUtils.js";
import ConfirmActionDialog from "../../components/ConfirmActionDialog.js";
import CollectionDialog from "./CollectionDialog.js";
import ListMenu from "../../components/ListMenu.js";
import VisibilityIcon from "../../components/VisibilityIcon.js";
import UserChip from "../../components/UserChip.js";
import { buildListPath, SHARE_ORIGIN } from "../../utils/detailPaths.js";
import { getStatusOptions, handleApiError } from "../../utils/utils.js";
import { useStatusSummary } from "../../hooks/useCustomQuery.js";
import { shareLink } from "../../utils/share.js";
import { burstFrom } from "../../utils/confetti.js";
import RollingNumber from "../../components/RollingNumber.js";

/**
 * The collection's finished percentage beside the finished icon. The number
 * rolls to each new value (see RollingNumber), and reaching 100% from below
 * pops the icon and bursts confetti from it — opening a list that's already
 * complete doesn't.
 */
function FinishedProgress({ pct, showLabel }) {
  const theme = useTheme();
  const statusOptions = getStatusOptions();
  const iconRef = useRef(null);
  const iconControls = useAnimationControls();
  const reduceMotion = useReducedMotion();
  const prevPctRef = useRef(pct);

  useEffect(() => {
    const prev = prevPctRef.current;
    prevPctRef.current = pct;
    if (pct !== 100 || prev === null || prev === 100 || reduceMotion) return;
    iconControls.start({
      scale: [1, 1.4, 1],
      transition: { duration: 0.45, ease: "easeOut" },
    });
    if (iconRef.current) {
      // Smaller and tighter than an achievement's burst: a nod, not a party
      burstFrom(iconRef.current, [theme.palette.status.finished.main], {
        particleCount: 30,
        startVelocity: 10,
        ticks: 80,
        scalar: 0.6,
      });
    }
  }, [pct, iconControls, reduceMotion, theme]);

  const label = showLabel ? " finished" : "";

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pr: 2 }}>
      <motion.span
        ref={iconRef}
        animate={iconControls}
        style={{ display: "flex" }}
      >
        {pct === 100
          ? statusOptions["finished"].iconFilled
          : statusOptions["finished"].icon}
      </motion.span>
      <Typography variant="body2" color="text.secondary">
        {/* Plain "—" while there's no value, so the first real one shows
            still rather than rolling up from 0 */}
        {pct === null ? "—" : <RollingNumber value={pct} />}%{label}
      </Typography>
    </Stack>
  );
}

export default function CollectionMetadata({
  editedList,
  originalList,
  onDelete,
  onAdd,
  isInline,
  onCollectionClose,
  onPendingInvalidation,
  showClose,
  readOnly,
}) {
  const theme = useTheme();
  const isAboveSm = useMediaQuery(theme.breakpoints.up("sm"));
  const { deleteList, saveList, unsaveList } = useApi();
  const { user, openAuthDialog } = useAuth();
  const isAdmin = Boolean(user?.isAdmin);
  const queryClient = useQueryClient();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [isSaved, setIsSaved] = useState(editedList.is_saved ?? false);

  const showAddButton = !editedList.size
    ? true
    : editedList.items.length < editedList.size;

  // Finished percentage — live via cache subscription, same mechanism as Cover.js.
  // useStatusSummary is a no-op for logged-out users (returns 0, isLoading false).
  const contentIds = useMemo(
    () => editedList.items.map((i) => i.content_id),
    [editedList.items],
  );
  const { finishedCount, isLoading: finishedLoading } =
    useStatusSummary(contentIds);
  // Guard on user so logged-out viewers render "—%" rather than "0%"
  const finishedPct =
    user && !finishedLoading && editedList.items.length > 0
      ? Math.round((finishedCount / editedList.items.length) * 100)
      : null;
  const finishedProgress = (
    <FinishedProgress pct={finishedPct} showLabel={isAboveSm} />
  );

  const saveListMutation = useMutation({
    mutationFn: (wasSaved) =>
      wasSaved ? unsaveList(editedList.list_id) : saveList(editedList.list_id),
    onSuccess: (data, wasSaved) => {
      // The menu closes on select, so the toast is the only confirmation the
      // save actually landed.
      toast.success(
        wasSaved
          ? "Removed from your saved collections"
          : "Added to your saved collections",
      );
      // Safe immediately regardless of wasSaved — this is the currently-open
      // list, so the key itself never disappears, only its is_saved field.
      queryClient.invalidateQueries({
        queryKey: ["list", editedList.list_id],
      });
      if (wasSaved) {
        // Defer invalidation to close — prevents a crash if the collection is
        // removed from collectionsData while the dialog is still open.
        onPendingInvalidation?.(() =>
          invalidateSavedCollections({ queryClient }),
        );
      } else {
        // Safe to invalidate immediately on save — the collection stays in view.
        invalidateSavedCollections({ queryClient });
      }
    },
    onError: (error, wasSaved) => {
      setIsSaved(wasSaved);
      handleApiError(error);
    },
  });

  const handleSaveToggle = () => {
    if (!user) {
      openAuthDialog("Saving collections");
      return;
    }
    const wasSaved = isSaved;
    setIsSaved(!wasSaved);
    saveListMutation.mutate(wasSaved);
  };

  // Shares the collection's URL — the same path the app routes on — via the
  // OS share sheet, falling back to copying it to the clipboard. Only the
  // owner's own visibility choice can make a share pointless/limited — a
  // read-only viewer can only ever see a list they're already entitled to.
  const handleShare = () => {
    if (!readOnly && editedList.visibility === "only_me") {
      toast.error(
        "This collection is only visible to you — change its visibility to share it",
      );
      return;
    }

    shareLink({
      // share_path is set when the dialog is showing a ?type= filtered view
      url: `${SHARE_ORIGIN}${editedList.share_path ?? buildListPath(editedList)}`,
      title: editedList.title,
      copiedMessage: `Link copied! Paste it anywhere to share ${
        readOnly ? "this collection" : "your collection"
      }${
        !readOnly && editedList.visibility === "followers"
          ? " — only your followers will be able to view it"
          : ""
      }`,
    });
  };

  // Standouts are shown read-only with the same byline/share/finished row as
  // other people's collections, but can't be saved (they're profile lists).
  const isStandouts = isStandoutsList(editedList);

  // Read-only viewers get save and share behind a single more button; the save
  // label/icon flips in place so the menu still reflects the saved state.
  const actionsMenuOptions = [
    ...(isStandouts
      ? []
      : [
          {
            label: isSaved ? "Saved" : "Save",
            value: "save",
            icon: isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />,
            disabled: saveListMutation.isPending,
          },
        ]),
    {
      label: "Share",
      value: "share",
      icon: <ShareIcon />,
    },
  ];

  const handleActionsMenuSelect = (value) => () => {
    if (value === "save") handleSaveToggle();
    if (value === "share") handleShare();
    setActionsAnchorEl(null);
  };

  const deleteListMutation = useMutation({
    mutationFn: (params) => deleteList(params),
    onSuccess: () => {
      invalidateListCaches({
        queryClient,
        listId: originalList.list_id,
        contentType: originalList.content_type,
        removed: true,
      });
      toast.success("Collection deleted");
      onDelete?.();
    },
  });

  const closeButton = (
    <Tooltip title="Close">
      <IconButton onClick={onCollectionClose} sx={{ p: 0.5 }}>
        <CloseIcon />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          pb: { xs: 0, sm: isInline ? 1.5 : 0 },
          position: "relative",
        }}
      >
        {/* pr: 4 reserves space for the close button which is always top-right */}
        <Box sx={{ pr: isInline ? 0 : 4 }}>
          <Typography
            sx={{
              textWrap: "balance",
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: isInline ? 3 : undefined,
              typography: { xs: "h6", sm: "body1", lg: "h6" },
              fontWeight: { xs: "bold", sm: "bold", lg: "bold" },
            }}
          >
            {editedList.title}
          </Typography>
        </Box>

        {!!editedList.description && (
          <Typography
            variant="body2"
            className={isInline ? undefined : "show-scrollbar"}
            sx={{
              display: "-webkit-box",
              overflow: isInline ? "hidden" : "auto",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: isInline ? 5 : undefined,
              maxHeight: 100,
            }}
          >
            {editedList.description}
          </Typography>
        )}

        {/* Actions row — two views, each a single line with a flex gap between left and right groups */}
        {!isInline && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            {!readOnly ? (
              // Owner view: [add · more] ............ [visibility · finished%]
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ ml: -0.5 }}
                >
                  {showAddButton && (
                    <Tooltip title="Add item to collection">
                      <IconButton onClick={onAdd} sx={{ p: 0.5 }}>
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Manage collection">
                    <IconButton
                      onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                      sx={{ p: 0.5 }}
                    >
                      <MoreHorizIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <VisibilityIcon
                      value={editedList.visibility}
                      noun="collection"
                      anchor="right"
                      color="text.secondary"
                    />
                  </Box>
                  {finishedProgress}
                </Stack>
              </>
            ) : (
              // Read-only view: [By <UserChip> · more] ............ [finished%]
              (editedList.origin === "user" ||
                editedList.origin === "canonical" ||
                isStandouts) && (
                <>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      By
                    </Typography>
                    <UserChip
                      username={
                        editedList.origin === "canonical"
                          ? "curate"
                          : editedList.username
                      }
                      avatarUrl={editedList.avatar_url}
                      color="text.secondary"
                    />
                    <Tooltip title={isStandouts ? "Share" : "Save or share"}>
                      <IconButton
                        onClick={(e) => setActionsAnchorEl(e.currentTarget)}
                        sx={{ p: 0.5, color: "text.secondary" }}
                      >
                        <MoreHorizIcon />
                      </IconButton>
                    </Tooltip>
                    {isAdmin && editedList.origin === "canonical" && (
                      <Tooltip title="Admin controls">
                        <IconButton
                          onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                          sx={{ p: 0.5, display: { xs: "none", sm: "inline-flex" } }}
                        >
                          <AdminPanelSettingsIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                  {finishedProgress}
                </>
              )
            )}
          </Stack>
        )}

        {/* Close button always sits top-right */}
        {!isInline && showClose && (
          <Box sx={{ position: "absolute", top: -1, right: 0 }}>
            {closeButton}
          </Box>
        )}
      </Box>

      <Menu
        id="collection-menu"
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={() => setMenuAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {/* Admin-only: lets admins add items to canonical collections from the
            read-only shield menu, since the owner-only Add icon button is hidden here. */}
        {isAdmin && readOnly && showAddButton && (
          <MenuItem
            dense
            onClick={() => {
              setMenuAnchorEl(null);
              onAdd();
            }}
          >
            <ListItemIcon>
              <AddIcon />
            </ListItemIcon>
            <ListItemText primary="Add" />
          </MenuItem>
        )}
        {/* Owners only — the admin shield menu that reuses this anchor is for
            management actions, and read-only viewers share from their own menu. */}
        {!readOnly && (
          <MenuItem
            dense
            onClick={() => {
              setMenuAnchorEl(null);
              handleShare();
            }}
          >
            <ListItemIcon>
              <ShareIcon />
            </ListItemIcon>
            <ListItemText primary="Share" />
          </MenuItem>
        )}
        <MenuItem
          dense
          onClick={() => {
            setMenuAnchorEl(null);
            setOpenEditDialog(true);
          }}
        >
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        <MenuItem dense onClick={() => setOpenConfirmDelete(true)}>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>

      <ListMenu
        anchorEl={actionsAnchorEl}
        onClose={() => setActionsAnchorEl(null)}
        onSelect={handleActionsMenuSelect}
        options={actionsMenuOptions}
      />

      <ConfirmActionDialog
        open={openConfirmDelete}
        onClose={() => {
          setOpenConfirmDelete(false);
          setMenuAnchorEl(null);
        }}
        title="Are you sure?"
        message="This collection will be permanently deleted"
        confirmColor="error"
        onConfirm={() => deleteListMutation.mutate(editedList.list_id)}
      />

      <CollectionDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        existingList={originalList}
      />
    </>
  );
}
